import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Container, Group, Text, Title } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CREATE_BILLING_PORTAL_SESSION,
  type CreateBillingPortalSessionData,
  type CreateBillingPortalSessionVars,
} from '@queries/billing';
import { GET_ME, type GetMeData } from '@queries/me';
import { useBillingCheckout } from '@hooks/useBillingCheckout';
import { redirectToUrl } from '@services/billing-redirect';
import { reportException, reportMessage } from '@services/error-reporting';

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Unable to connect to billing right now. Please try again.';
}

function Billing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [inlineError, setInlineError] = useState('');

  const { startBillingCheckout, isLoading: isCheckoutLoading } = useBillingCheckout();
  const [openPortal, portalState] = useMutation<CreateBillingPortalSessionData, CreateBillingPortalSessionVars>(
    CREATE_BILLING_PORTAL_SESSION
  );
  const { data: meData } = useQuery<GetMeData>(GET_ME);

  const isBusy = isCheckoutLoading || portalState.loading;
  const hasCurrentSubscription = Boolean(meData?.me?.billingSubscriptionExternalId);
  const canStartSubscription = !hasCurrentSubscription;
  const billingStatus = String(meData?.me?.billingStatus || '').toLowerCase();
  const checkoutResult = (searchParams.get('checkout') || '').toLowerCase();
  const checkoutButtonLabel =
    billingStatus === 'active' || billingStatus === 'trialing' ? 'Change Plan' : 'Start Subscription';
  const checkoutMessage =
    checkoutResult === 'success'
      ? 'Checkout complete. Your billing status will update shortly.'
      : checkoutResult === 'cancel'
        ? 'Checkout canceled.'
        : '';

  useEffect(() => {
    if (!checkoutResult) {
      return;
    }

    const timer = window.setTimeout(() => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('checkout');
      const search = nextParams.toString();
      navigate({ search: search ? `?${search}` : '' }, { replace: true });
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [checkoutResult, navigate, searchParams]);

  const handleCheckout = async () => {
    if (!canStartSubscription) {
      return;
    }

    setInlineError('');

    await startBillingCheckout({
      onMissingPriceId: () => {
        const message = 'Billing is not configured yet. Please contact support.';
        setInlineError(message);
        reportMessage(message, 'billing.checkout', 'warning');
      },
      onMissingRedirectUrl: () => {
        const message = 'Unable to start checkout right now. Please try again.';
        setInlineError(message);
        reportMessage(message, 'billing.checkout');
      },
      onError: (error) => {
        setInlineError(toErrorMessage(error));
        reportException(error, 'billing.checkout');
      },
    });
  };

  const handleManageBilling = async () => {
    if (!hasCurrentSubscription) {
      return;
    }

    setInlineError('');

    try {
      const result = await openPortal({
        variables: {
          returnUrl: new URL('/billing', window.location.origin).toString(),
        },
      });

      const redirectUrl = result?.data?.createBillingPortalSession?.url;
      if (!redirectUrl) {
        setInlineError('Unable to open billing management right now. Please try again.');
        return;
      }

      redirectToUrl(redirectUrl);
    } catch (error) {
      setInlineError(toErrorMessage(error));
      reportException(error, 'billing.portal');
    }
  };

  return (
    <Container size="sm" py="xl">
      <Title order={2} ta="center">
        Billing
      </Title>
      <Text c="dimmed" size="md" ta="center" mt="sm">
        Payments are handled by Stripe-hosted pages for secure checkout and subscription management.
      </Text>

      {checkoutMessage ? (
        <Text c={checkoutResult === 'success' ? 'green' : 'yellow'} size="sm" ta="center" mt="md">
          {checkoutMessage}
        </Text>
      ) : null}

      {inlineError ? (
        <Text c="red" size="sm" ta="center" mt="md">
          {inlineError}
        </Text>
      ) : null}

      <Group justify="center" mt="xl">
        <Button onClick={handleCheckout} disabled={isBusy || !canStartSubscription} loading={isCheckoutLoading}>
          {checkoutButtonLabel}
        </Button>
        <Button
          variant="outline"
          onClick={handleManageBilling}
          disabled={isBusy || !hasCurrentSubscription}
          loading={portalState.loading}
        >
          Manage Billing
        </Button>
      </Group>
    </Container>
  );
}

export default Billing;
